import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import StarRating from "@/components/StarRating";
import { useAuth } from "@/context/AuthContext";
import { fetchReviews, submitReview, deleteReview, formatApiErrorDetail } from "@/lib/api";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
};

export default function ReviewSection({ entityType, entityId }) {
  const { user } = useAuth();
  const [data, setData] = useState({ count: 0, average: 0, reviews: [] });
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => fetchReviews(entityType, entityId).then(setData).catch(() => {});
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  const myReview = user ? data.reviews.find((r) => r.user_id === user.user_id) : null;
  useEffect(() => {
    if (myReview) {
      setRating(myReview.rating);
      setComment(myReview.comment || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myReview?.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) {
      toast.error("Please select a star rating");
      return;
    }
    setBusy(true);
    try {
      await submitReview({ entity_type: entityType, entity_id: entityId, rating, comment });
      toast.success(myReview ? "Review updated" : "Thanks for your review!");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit review");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await deleteReview(id);
      toast.success("Review removed");
      setRating(0);
      setComment("");
      await load();
    } catch {
      toast.error("Could not remove review");
    }
  };

  return (
    <section data-testid="review-section" className="bg-white border-t border-sand-200 py-20">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-3">Community Reviews</div>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-sand-900 leading-none">
              {data.count > 0 ? data.average.toFixed(1) : "—"}
              <span className="text-2xl text-sand-600"> / 5</span>
            </h2>
          </div>
          <div className="text-left sm:text-right">
            <StarRating value={data.average} size={22} />
            <div className="mt-2 font-mono text-[10px] tracking-widest uppercase text-sand-600" data-testid="review-count">
              {data.count} {data.count === 1 ? "review" : "reviews"}
            </div>
          </div>
        </div>

        {/* Write a review */}
        {user ? (
          <form onSubmit={submit} className="border border-sand-200 p-6 mb-12" data-testid="review-form">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-sand-700 mb-3">
              {myReview ? "Update your review" : "Write a review"}
            </div>
            <div className="flex items-center gap-1.5 mb-4" onMouseLeave={() => setHover(0)}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  type="button"
                  key={n}
                  data-testid={`review-star-${n}`}
                  onMouseEnter={() => setHover(n)}
                  onClick={() => setRating(n)}
                  className="transition-transform active:scale-90"
                  aria-label={`${n} star`}
                >
                  <Star
                    size={28}
                    className={n <= (hover || rating) ? "fill-ochre-400 text-ochre-400" : "fill-none text-sand-300"}
                  />
                </button>
              ))}
            </div>
            <textarea
              data-testid="review-comment-input"
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your experience…"
              className="w-full border border-sand-300 px-4 py-3 bg-white focus:border-brand-500 outline-none mb-4"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="review-submit-button"
              className="bg-sand-900 text-sand-50 px-6 py-3 font-mono text-[11px] font-bold tracking-[0.2em] uppercase hover:bg-brand-500 transition-colors disabled:opacity-60"
            >
              {busy ? "Submitting…" : myReview ? "Update Review" : "Post Review"}
            </button>
          </form>
        ) : (
          <div className="border border-dashed border-sand-300 p-6 mb-12 text-center" data-testid="review-signin-prompt">
            <p className="font-serif italic text-xl text-sand-700">
              <Link to="/login" className="text-brand-500 editorial-link">Sign in</Link> to leave a review.
            </p>
          </div>
        )}

        {/* Reviews list */}
        {data.reviews.length === 0 ? (
          <p className="font-serif italic text-xl text-sand-600" data-testid="no-reviews">
            No reviews yet — be the first to weigh in.
          </p>
        ) : (
          <div className="space-y-8" data-testid="reviews-list">
            {data.reviews.map((rev) => (
              <div key={rev.id} className="border-b border-sand-200 pb-8" data-testid={`review-item-${rev.id}`}>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <StarRating value={rev.rating} size={15} />
                    <span className="font-serif text-lg font-bold text-sand-900">{rev.user_name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[10px] tracking-widest uppercase text-sand-500">{fmtDate(rev.created_at)}</span>
                    {user && (rev.user_id === user.user_id || user.role === "admin") && (
                      <button
                        onClick={() => onDelete(rev.id)}
                        data-testid={`delete-review-${rev.id}`}
                        className="text-sand-400 hover:text-brand-500 transition-colors"
                        aria-label="Delete review"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
                {rev.comment && <p className="mt-3 text-sand-700 font-light leading-relaxed">{rev.comment}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
